import React from 'react'
import { Grid } from '@mui/material'
import { deepObjectCopy } from 'widgets-shared/utilities'
import MarketSelection from 'smartstream-widgets/controls/MarketSelection/MarketSelection'
import VerticalScoreBoard from 'smartstream-widgets/components/VerticalScoreBoard/VerticalScoreBoard'
import { safelyGetNestedValue } from 'widgets-shared/utilities'
import { getHighestPriceInMarkets } from 'smartstream-widgets/utils/markets'
import { withStyles, createStyles } from '@mui/styles'
import { Theme, alpha  } from '@mui/material/styles'
import { Market, MarketAllowEmpty, MarketSelectionType } from 'smartstream-widgets/types/market'
import { Competitor, CompetitorWithFixtureData, Records } from 'smartstream-widgets/types/competitor'
import { OddsFormat, FixtureData, MatchSummaries } from '../types.js'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
  bookmakers: Array<string>,
  classes: {
    [K in string]: string
  },
  data: FixtureData,
  oddsFormat: OddsFormat,
  hideStartTime?: boolean,
  isAwayTeamFirst?: boolean,
  position?: number,
  showCompetitorPerRow?: boolean,
  mobileView?: boolean
}

const styles = (theme: Theme) => createStyles({
  bestSelection: {
    backgroundColor: alpha(theme.palette.success.main, 0.11),
    border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
    color: theme.palette.success.main,
    '&:hover': {
      background: theme.palette.success.main,
      '& $captionOdd': {
        color: theme.palette.common.white
      }
    },
    '&$buttonDisabled': {
      '& $captionOdd': {
        color: theme.palette.success.main
      }
    }
  },
  buttonUp: {},
  buttonDisabled: {},
  buttonDown: {},
  captionOdd: {
    fontSize: 12
  },
  dash: {
    alignItems: 'center',
    display: 'inline-flex',
    minWidth: 72,
    minHeight: 33,
    '&::after': {
      margin: '0 auto',
      content: '""',
      backgroundColor: theme.palette.action.disabled,
      width: 19,
      height: 1
    }
  },
  marketSelection: {
    margin: `4px 0px`,
    maxWidth: '176px'
  },
  root: {
    minWidth: '100%',
    width: 'min-content'
  },
  rootMarketSelection: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center'
  },
  rootMobileMarketSelection: {
    position: 'relative'
  },
  scoreboard: {
    background: 'inherit',
    boxSizing: 'border-box',
    borderRight: `1px solid ${theme.palette.divider}`,
    left: 0,
    position: 'sticky',
    top: 0,
    width: 170,
    zIndex: 1
  },
  selectionsContainer: {
    flexGrow: 1,
    width: 'min-content'
  },
  selectionsColumn: {
    width: 110,
    margin: `6px 0px`,
    paddingRight: 6
  },
  liveCaption: {
    display: 'none'
  }
})

function matchCompetitorInfo(
  competitors: Array<Competitor>,
  records: Array<Records>,
  matchSummaries: MatchSummaries | null
): Array<CompetitorWithFixtureData> {
  return (deepObjectCopy(competitors) as Array<Competitor>).reduce((acc: Array<CompetitorWithFixtureData>, comp: CompetitorWithFixtureData, i) => {
    const compRecords = records && records.find(rec => rec.competitorId === comp.competitorId)
    comp.records = ((compRecords || {}) as Records)
    const side = comp.homeOrAway || (i === 0 ? 'HOME' : 'AWAY')
    const sideScore = side === 'HOME' ? 'homeScore' : 'awayScore'
    comp.currentScore = (matchSummaries && matchSummaries[sideScore]) || 0
    acc.push(comp)
    return acc
  }, [])
}

export function orderMarketsAndSelections(
  bookmakers: Array<string>,
  markets: Array<Market>,
  competitors: Array<CompetitorWithFixtureData>,
  draw: boolean,
  isAwayTeamFirst: boolean
): Array<MarketAllowEmpty> {
  const bookmakerIds = Array.isArray(bookmakers) && bookmakers.length > 0 && bookmakers
  if (!bookmakerIds || !Array.isArray(markets)) return []
  const isSingleCompetitor = competitors.length === 1
  const emptySelections = (isSingleCompetitor ? [null] : Array(draw ? 3 : 2).fill(null)) as Array<null>
  return bookmakerIds.reduce((acc: Array<MarketAllowEmpty>, bookmaker: string) => {
    const match = markets.find(market => market.sourceId === bookmaker)
    if (match) {
      const newMatch = JSON.parse(JSON.stringify(match)) as MarketAllowEmpty
      if (isSingleCompetitor) {
        const matchedSelection = match.selections.find(sel => {
          return sel && (sel.competitorId === competitors[0].competitorId)
        })
        newMatch.selections = [matchedSelection]
      } else {
        const homeCompetitor = competitors.find(competitor => competitor.homeOrAway === 'HOME') || competitors[0]
        const homeSelection = match.selections.find(sel => {
          return sel && (sel.competitorId === homeCompetitor.competitorId || sel.outcome === 'Over')
        })
        const awayCompetitor = competitors.find(competitor => competitor.homeOrAway === 'AWAY') || competitors[1]
        const awaySelection = match.selections.find(sel => {
          return sel && (sel.competitorId === awayCompetitor.competitorId || sel.outcome === 'Under')
        })
        newMatch.selections = isAwayTeamFirst ? [awaySelection, homeSelection] : [homeSelection, awaySelection]
        if (draw) {
          const drawSelection = match.selections.find(sel => {
            return sel && (sel.outcome === 'Draw' || !sel.competitorId)
          })
          newMatch.selections.push(drawSelection)
        }
      }
      acc.push(newMatch)
    } else {
      acc.push({ sourceId: bookmaker, marketTypeId: '', selections: emptySelections })
    }
    return acc
  }, [])
}

export function ScoreBoardSelections(props: Props) {
  const { classes, oddsFormat, bookmakers, hideStartTime, isAwayTeamFirst = false, position, showCompetitorPerRow, mobileView } = props
  const { competitionId, records = [], competitors = [], markets, matchSummaries, scheduledStartTime } = props.data
  const hasDraws = (safelyGetNestedValue(markets, ['0', 'selections'], []) as Array<MarketSelectionType>).length === 3
  const groupedCompetitorInfo = matchCompetitorInfo(showCompetitorPerRow ? [competitors[0]] : competitors, records, matchSummaries)
  const bookmakersMarkets = orderMarketsAndSelections(bookmakers, markets, groupedCompetitorInfo, hasDraws, isAwayTeamFirst)
  const highestPrice = getHighestPriceInMarkets(bookmakersMarkets)
  const selectionClasses = (isHighest: boolean) => {
    return {
      oddButton: classes.marketSelection,
      buttonBase: `${isHighest ? classes.bestSelection : ''}`,
      buttonDisabled: classes.buttonDisabled,
      buttonDown: classes.buttonDown,
      buttonUp: classes.buttonUp,
      captionOdd: classes.captionOdd,
      root: mobileView ? classes.rootMobileMarketSelection : classes.rootMarketSelection
    }
  }
  return (
    <Grid className={classes.root} container wrap='nowrap' data-testid='scoreboard-selection'>
      <VerticalScoreBoard
        competitionId={competitionId}
        isAwayTeamFirst={isAwayTeamFirst}
        position={position}
        competitors={groupedCompetitorInfo}
        classes={{ root: classes.scoreboard, liveCaption: classes.liveCaption }}
        hasDraws={hasDraws}
        matchState={matchSummaries}
        scheduledStartTime={!hideStartTime && scheduledStartTime} />
      <Grid container className={classes.selectionsContainer} justifyContent='space-around' wrap='nowrap'>
        {bookmakersMarkets.map((bookmaker: MarketAllowEmpty) => {
          const { selections, sourceId, marketTypeId } = bookmaker
          return (
            <Grid
              data-testid='selections-container'
              alignItems='center'
              className={classes.selectionsColumn}
              container
              direction='column'
              justifyContent='center'
              key={`${sourceId}-${marketTypeId}`}
              wrap='nowrap'
            >
              {selections.map((sel, i) => {
                if (sel) {
                  const isHighest = highestPrice === sel.price.decimal
                  return (<MarketSelection
                    classes={selectionClasses(isHighest)}
                    sourceId={sourceId}
                    oddsFormat={oddsFormat}
                    selection={sel}
                    key={`${sel.name}-${marketTypeId}-${sourceId}`}
                  />)
                }
                return (<div className={`${classes.marketSelection} ${classes.dash}`} key={`${sourceId}-${marketTypeId}-${i}`}  data-testid='dash' />)
              })}
            </Grid>
          )
        })}
      </Grid>
    </Grid>
  )
}

export default withStyles(styles, { name: 'ScoreBoardSelections', defaultTheme: getDefaultTheme })(ScoreBoardSelections)
